const STORAGE_KEY = 'saas_session';

const LEGACY_MODULE_CAPABILITIES = {
  financeiro: ['master_data', 'billing_core', 'collections'],
  construtor: ['master_data'],
  radar_lucro: [],
  solar: ['billing_core', 'collections'],
};

const FALLBACK_MODULE_ORDER = ['financeiro', 'solar', 'construtor', 'radar_lucro'];
const FALLBACK_MODULE_ROUTES = {
  financeiro: '/dashboard',
  solar: '/solar/dashboard',
  construtor: '/construtor',
  radar_lucro: '/radar-lucro',
};

export function getSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

export function setSession(session) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEY);
}

export function isAuthenticated() {
  const s = getSession();
  return !!(s && s.token && s.tenantId && s.empresaId);
}

export function getAuthHeaders() {
  const s = getSession();
  if (!s?.token) return {};

  return {
    Authorization: `Bearer ${s.token}`,
    'x-tenant-id': s.tenantId,
    'x-empresa-id': s.empresaId,
  };
}

export function switchEmpresa(empresaId) {
  const s = getSession();
  if (!s) return;
  s.empresaId = empresaId;
  setSession(s);
}

export function getEmpresaAtual() {
  const s = getSession();
  if (!s?.empresaId || !Array.isArray(s.empresas)) return null;
  return s.empresas.find((e) => e.empresa_id === s.empresaId) || null;
}

function getEmpresaModulesMap(empresa) {
  if (!empresa) return {};

  if (empresa.modules_map && typeof empresa.modules_map === 'object') {
    return Object.keys(empresa.modules_map).reduce((acc, key) => {
      acc[key] = empresa.modules_map[key]?.enabled === true;
      return acc;
    }, {});
  }

  if (Array.isArray(empresa.modules)) {
    return empresa.modules.reduce((acc, item) => {
      const key = String(item?.key || '').trim().toLowerCase();
      if (key) acc[key] = item?.enabled === true;
      return acc;
    }, {});
  }

  return {
    financeiro: empresa.modulo_financeiro_ativo === true,
    construtor: empresa.modulo_construtor_ativo === true,
    radar_lucro: empresa.modulo_radar_lucro_ativo === true,
    solar: empresa.modulo_solar_ativo === true,
  };
}

function getEmpresaCapabilitiesMap(empresa) {
  if (!empresa) return {};

  if (empresa.capabilities_map && typeof empresa.capabilities_map === 'object') {
    return Object.keys(empresa.capabilities_map).reduce((acc, key) => {
      acc[key] = empresa.capabilities_map[key] === true;
      return acc;
    }, {});
  }

  if (Array.isArray(empresa.capability_keys)) {
    return empresa.capability_keys.reduce((acc, key) => {
      const normalized = String(key || '').trim();
      if (normalized) acc[normalized] = true;
      return acc;
    }, {});
  }

  const modulesMap = getEmpresaModulesMap(empresa);
  return Object.entries(modulesMap).reduce((acc, [moduleKey, enabled]) => {
    if (!enabled) return acc;
    for (const capability of LEGACY_MODULE_CAPABILITIES[moduleKey] || []) {
      acc[capability] = true;
    }
    return acc;
  }, {});
}

export function getEnabledModules(empresa = getEmpresaAtual()) {
  const modulesMap = getEmpresaModulesMap(empresa);

  if (Array.isArray(empresa?.modules) && empresa.modules.length > 0) {
    return empresa.modules
      .filter((item) => item?.enabled === true)
      .sort((a, b) => Number(a?.sort_order || 100) - Number(b?.sort_order || 100));
  }

  return FALLBACK_MODULE_ORDER.filter((key) => modulesMap[key] === true).map((key, index) => ({
    key,
    nome: key,
    enabled: true,
    sort_order: (index + 1) * 10,
    default_route: FALLBACK_MODULE_ROUTES[key] || '/',
  }));
}

export function hasModulo(modulo) {
  const empresa = getEmpresaAtual();
  const modulesMap = getEmpresaModulesMap(empresa);
  return modulesMap[modulo] === true;
}

export function hasCapability(capability) {
  const empresa = getEmpresaAtual();
  const capabilitiesMap = getEmpresaCapabilitiesMap(empresa);
  return capabilitiesMap[capability] === true;
}

export function getPerfilEmpresaAtual() {
  const empresa = getEmpresaAtual();
  return String(empresa?.perfil || '').toLowerCase();
}

export function canWriteEmpresaData() {
  const s = getSession();
  const roleGlobal = String(s?.user?.role_global || '').toLowerCase();
  if (roleGlobal === 'owner' || roleGlobal === 'admin') return true;
  const perfil = getPerfilEmpresaAtual();
  return ['owner', 'admin', 'financeiro', 'colaborador'].includes(perfil);
}

export function getPreferredStartRoute(empresa = getEmpresaAtual()) {
  const enabledModules = getEnabledModules(empresa);
  const firstModule = enabledModules.find((item) => item?.default_route);
  if (firstModule?.default_route) return firstModule.default_route;

  const capabilitiesMap = getEmpresaCapabilitiesMap(empresa);
  if (capabilitiesMap.billing_core) return '/cobrancas';
  if (capabilitiesMap.master_data) return '/cadastros/empresa';
  return '/login';
}
